import React, { useState, useEffect } from 'react'
import { detectNoteType, isSinglePurposeNote } from '../../utils/noteTypeDetection'
import EditNotePage from '../../pages/notes/EditNotePage'
import { useTabs } from '../../contexts/TabsContext'
import { useNotes } from '../../contexts/NotesContext'
import { useNavigate } from 'react-router-dom'
import { useLocalMode } from '../../contexts/LocalModeContext'

// Lazy load specialized editors to improve performance
const FlowchartNoteEditor = React.lazy(() => import('./FlowchartNoteEditor'))

/**
 * EditorRegistry Component
 * 
 * Routes note types to appropriate specialized editors.
 * For single-purpose notes, uses specialized editors.
 * For standard notes, uses the existing EditNotePage.
 */
const EditorRegistry = ({ noteId, tabId = null, isInTab = false, note = null, ...props }) => {
  const { closeTab } = useTabs()
  const { updateNote, createNote } = useNotes()
  const navigate = useNavigate()
  const { isLocalMode, getLocalNote } = useLocalMode()
  const [loadedNote, setLoadedNote] = useState(note)
  const [isLoading, setIsLoading] = useState(false)
  
  // Detect note type
  const noteType = loadedNote ? detectNoteType(loadedNote) : 'STANDARD'
  
  // Load complete note data if we only have partial data
  useEffect(() => {
    const loadCompleteNoteData = async () => {
      // If we already have complete note data with noteId, no need to load
      if (loadedNote && (loadedNote.noteId || loadedNote.id)) {
        return
      }
      
      // If we have a noteId but incomplete note data, load the complete note
      if (noteId && (!loadedNote || !loadedNote.noteId)) {
        setIsLoading(true)
        try {
          let completeNoteData
          if (isLocalMode) {
            completeNoteData = await getLocalNote(noteId)
          } else {
            // For cloud mode, we'd need to fetch from API
            // For now, we'll work with what we have
            completeNoteData = loadedNote || { noteId: noteId, id: noteId }
          }
          
          if (completeNoteData) {
            setLoadedNote(completeNoteData)
          }
        } catch (error) {
          console.error('Failed to load complete note data:', error)
        } finally {
          setIsLoading(false)
        }
      }
    }
    
    loadCompleteNoteData()
  }, [noteId, loadedNote, isLocalMode, getLocalNote])
  
  // Handle save for single-purpose notes
  const handleSinglePurposeNoteSave = async (noteData) => {
    try {
      // Extract noteId from noteData for updateNote function
      // Priority: noteData.noteId -> noteData.id -> noteId prop -> loadedNote.noteId -> loadedNote.id
      const noteIdToUpdate = noteData.noteId || noteData.id || noteId || loadedNote?.noteId || loadedNote?.id
      
      // Determine if this is a new note or an existing note
      const isExistingNote = noteIdToUpdate && !noteIdToUpdate.startsWith('new-') && !noteIdToUpdate.startsWith('temp-')
      
      if (isExistingNote) {
        // Update existing note
        console.log('🔍 EditorRegistry: Updating existing note with ID:', noteIdToUpdate, 'noteData:', noteData)
        const savedNote = await updateNote(noteIdToUpdate, noteData)
        closeTab(tabId)
        // Navigate to the saved note
        if (savedNote && savedNote.noteId) {
          navigate(`/notes/${savedNote.noteId}`)
        }
      } else {
        // Create new note
        console.log('🔍 EditorRegistry: Creating new note:', noteData)
        const savedNote = await createNote(noteData)
        closeTab(tabId)
        // Navigate to the saved note
        if (savedNote && savedNote.noteId) {
          navigate(`/notes/${savedNote.noteId}`)
        }
      }
    } catch (error) {
      console.error('Failed to save single purpose note:', error)
      throw error
    }
  }

  // Handle cancel for single-purpose notes
  const handleSinglePurposeNoteCancel = () => {
    closeTab(tabId)
  }
  
  // Show loading state while loading note data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading note data...</p>
        </div>
      </div>
    )
  }
  
  // Route to appropriate editor
  const renderEditor = () => {
    // Check if this is a single-purpose note that needs a specialized editor
    if (isSinglePurposeNote(noteType)) {
      switch (noteType) {
        case 'FLOWCHART':
          return (
            <React.Suspense fallback={
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading flowchart editor...</p>
                </div>
              </div>
            }>
              <FlowchartNoteEditor 
                note={loadedNote}
                onSave={handleSinglePurposeNoteSave}
                onCancel={handleSinglePurposeNoteCancel}
                isInTab={isInTab}
                tabId={tabId}
                {...props}
              />
            </React.Suspense>
          )
        
        // Add other single-purpose note types here as needed
        // case 'HANDWRITING':
        //   return <HandwritingNoteEditor ... />
        // case 'VOICE':
        //   return <VoiceNoteEditor ... />
        
        default:
          // Fallback to standard editor for unknown single-purpose types
          return <EditNotePage tabId={tabId} noteId={noteId} />
      }
    }
    
    // For standard notes, use the existing EditNotePage
    return <EditNotePage tabId={tabId} noteId={noteId} />
  }
  
  return renderEditor()
}

/**
 * Get the appropriate editor component for a note type
 * @param {string} noteType - The type of note
 * @returns {React.Component} The appropriate editor component
 */
export const getEditorForNoteType = (noteType) => {
  if (isSinglePurposeNote(noteType)) {
    switch (noteType) {
      case 'FLOWCHART':
        return FlowchartNoteEditor
      default:
        return EditNotePage // Fallback for unknown single-purpose types
    }
  }
  
  // Standard notes always use EditNotePage
  return EditNotePage
}

/**
 * Check if a note type has a specialized editor
 * @param {string} noteType - The type of note
 * @returns {boolean} True if a specialized editor exists
 */
export const hasSpecializedEditor = (noteType) => {
  return isSinglePurposeNote(noteType)
}

EditorRegistry.displayName = 'EditorRegistry'

export default EditorRegistry